import { PositionsGetMetricsHistoryResponseType, PositionsGetMetricsResponseType } from '@/shared/backend/types'
import { round } from '@kunalabs-io/kai'
import { DefaultTheme } from 'styled-components'

import { CurrentMetrics } from './MetricsOverview'
import { ActiveMetric } from './Analytics'
import { DataPoint } from './MetricsHistoryGraphContainer'
import { getFormattedUsdValue } from 'utils/usdFormatters'
import { getOrdinalSuffix } from 'utils/helpers'
import { POSITION_METRICS_GAP_THRESHOLD_MS } from 'utils/chartGapUtils'

const getPctValue = (value: number) => `${round(value * 100, 2) + '%'}`

export const getOverviewMetrics = (theme: DefaultTheme, metricsData?: PositionsGetMetricsResponseType) => {
  if (!metricsData) {
    return []
  }

  return [
    {
      value: getFormattedUsdValue(metricsData.equity),
      name: 'Equity',
      tooltipText: 'Your share of the position’s value, calculated as total assets minus debt.',
    },
    ...(typeof metricsData.apr1h.apr !== 'undefined'
      ? [
          {
            value: getPctValue(metricsData.apr1h.apr),
            name: 'APR',
            tooltipText: (
              <div>
                <div style={{ borderBottom: `1px solid ${theme.colors.text.primaryDarkBackground}`, paddingBottom: 6 }}>
                  Your net leveraged APR: rewards earned minus borrowing costs.
                </div>
                <div style={{ paddingTop: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>1h average</div>
                    <div>{getPctValue(metricsData.apr1h.apr)}</div>
                  </div>
                  {typeof metricsData?.apr24h?.apr === 'number' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div>24h average</div>
                      <div>{getPctValue(metricsData.apr24h.apr)}</div>
                    </div>
                  )}
                  {typeof metricsData?.aprFull?.apr === 'number' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div>All-time average</div>
                      <div>{getPctValue(metricsData.aprFull.apr)}</div>
                    </div>
                  )}
                </div>
              </div>
            ),
          },
        ]
      : []),
    {
      value: getFormattedUsdValue(metricsData.pnl.totalPnl),
      name: 'PnL',
      tooltipText:
        'Net result of your position, factoring in yield, price impact, and borrowing costs — your take-home profit or loss.',
      pctDiff: `(${getPctValue(metricsData.pnl.returnOnEquity)})`,
      isValueNegative: metricsData.pnl.returnOnEquity < 0,
    },
    {
      value: getFormattedUsdValue(metricsData.pnl.netYieldValue),
      name: 'Net yield',
      tooltipText:
        'Total rewards and fees earned on your leveraged position (PnL = Net Yield – Price Divergence Impact).',
      pctDiff: `(${getPctValue(metricsData.pnl.yieldOnEquity)})`,
      isValueNegative: metricsData.pnl.yieldOnEquity < 0,
    },
    {
      value: getFormattedUsdValue(metricsData.pnl.divergenceImpact),
      name: 'Price divergence impact',
      tooltipText:
        'Impact of pool price change on PnL, influenced by price direction and the position structure. That is, whether the asset price change increased or decreased PnL, and by how much.',
      pctDiff: `(${getPctValue(metricsData.pnl.divergenceImpactOnEquity)})`,
      isValueNegative: metricsData.pnl.divergenceImpactOnEquity < 0,
    },
    {
      value: getFormattedUsdValue(metricsData.pnl.hodlBenchmarkPnl),
      name: 'HODL benchmark',
      tooltipText: 'The gain or loss versus simply holding the tokens.',
      pctDiff: `(${getPctValue(metricsData.pnl.hodlBenchmarkReturnRatio)})`,
      isValueNegative: metricsData.pnl.hodlBenchmarkReturnRatio < 0,
    },
  ]
}

export const getCurrentMetrics = (
  theme: DefaultTheme,
  xSymbol: string,
  ySymbol: string,
  activeMetric: ActiveMetric,
  metricsData?: PositionsGetMetricsResponseType
): CurrentMetrics[] => {
  if (!metricsData) {
    return []
  }
  switch (activeMetric) {
    case 'position-size':
      return [
        {
          value: getFormattedUsdValue(metricsData.lpSize),
          name: 'Position size',
          tooltipText: 'Total value of the position, including both your equity and any borrowed assets.',
        },
        {
          value: getFormattedUsdValue(metricsData.equity),
          name: 'Equity',
          tooltipText: 'Your share of the position’s value, calculated as total assets minus debt.',
        },
        {
          value: getFormattedUsdValue(metricsData.debt),
          name: 'Debt',
          tooltipText: 'Total value of assets you’ve borrowed to leverage this position.',
        },
      ]
    case 'lp-apr':
      return [
        ...(typeof metricsData.apr1h.lpApr !== 'undefined'
          ? [
              {
                value: getPctValue(metricsData.apr1h.lpApr),
                name: 'LP APR',
                tooltipText: (
                  <div>
                    <div
                      style={{ borderBottom: `1px solid ${theme.colors.text.primaryDarkBackground}`, paddingBottom: 6 }}
                    >
                      APR you would earn by providing liquidity in this range directly to the underlying DEX pool
                      (without leverage).
                    </div>
                    <div style={{ paddingTop: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>1h average</div>
                        <div>{getPctValue(metricsData.apr1h.lpApr)}</div>
                      </div>
                      {typeof metricsData?.apr24h?.lpApr === 'number' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>24h average</div>
                          <div>{getPctValue(metricsData.apr24h.lpApr)}</div>
                        </div>
                      )}
                      {typeof metricsData?.aprFull?.lpApr === 'number' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>All-time average</div>
                          <div>{getPctValue(metricsData.aprFull.lpApr)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ),
              },
            ]
          : []),
        ...(typeof metricsData.apr1h.lpRewardsApr !== 'undefined'
          ? [
              {
                value: getPctValue(metricsData.apr1h.lpRewardsApr),
                name: 'LP rewards APR',
                tooltipText: (
                  <div>
                    <div
                      style={{ borderBottom: `1px solid ${theme.colors.text.primaryDarkBackground}`, paddingBottom: 6 }}
                    >
                      APR you would earn from additional rewards (e.g. liquidity incentives) by providing liquidity in
                      this range directly to the underlying DEX pool (without leverage).
                    </div>
                    <div style={{ paddingTop: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>1h average</div>
                        <div>{getPctValue(metricsData.apr1h.lpRewardsApr)}</div>
                      </div>
                      {typeof metricsData?.apr24h?.lpRewardsApr === 'number' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>24h average</div>
                          <div>{getPctValue(metricsData.apr24h.lpRewardsApr)}</div>
                        </div>
                      )}
                      {typeof metricsData?.aprFull?.lpRewardsApr === 'number' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>All-time average</div>
                          <div>{getPctValue(metricsData.aprFull.lpRewardsApr)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ),
              },
            ]
          : []),
        ...(typeof metricsData.apr1h.lpFeesApr !== 'undefined'
          ? [
              {
                value: getPctValue(metricsData.apr1h.lpFeesApr),
                name: 'LP fees APR',
                tooltipText: (
                  <div>
                    <div
                      style={{ borderBottom: `1px solid ${theme.colors.text.primaryDarkBackground}`, paddingBottom: 6 }}
                    >
                      APR you would earn from trading fees by providing liquidity in this range directly to the
                      underlying DEX pool (without leverage).
                    </div>
                    <div style={{ paddingTop: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>1h average</div>
                        <div>{getPctValue(metricsData.apr1h.lpFeesApr)}</div>
                      </div>
                      {typeof metricsData?.apr24h?.lpFeesApr === 'number' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>24h average</div>
                          <div>{getPctValue(metricsData.apr24h.lpFeesApr)}</div>
                        </div>
                      )}
                      {typeof metricsData?.aprFull?.lpFeesApr === 'number' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>All-time average</div>
                          <div>{getPctValue(metricsData.aprFull.lpFeesApr)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ),
              },
            ]
          : []),
      ]
    case 'apr':
      return [
        ...(typeof metricsData.apr1h.apr !== 'undefined'
          ? [
              {
                value: getPctValue(metricsData.apr1h.apr),
                name: 'APR',
                tooltipText: (
                  <div>
                    <div
                      style={{ borderBottom: `1px solid ${theme.colors.text.primaryDarkBackground}`, paddingBottom: 6 }}
                    >
                      Your net leveraged APR: rewards earned minus borrowing costs.
                    </div>
                    <div style={{ paddingTop: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>1h average</div>
                        <div>{getPctValue(metricsData.apr1h.apr)}</div>
                      </div>
                      {typeof metricsData?.apr24h?.apr === 'number' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>24h average</div>
                          <div>{getPctValue(metricsData.apr24h.apr)}</div>
                        </div>
                      )}
                      {typeof metricsData?.aprFull?.apr === 'number' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>All-time average</div>
                          <div>{getPctValue(metricsData.aprFull.apr)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ),
              },
            ]
          : []),
        ...(typeof metricsData.apr1h.rewardsApr !== 'undefined'
          ? [
              {
                value: getPctValue(metricsData.apr1h.rewardsApr),
                name: 'Rewards APR',
                tooltipText: (
                  <div>
                    <div
                      style={{ borderBottom: `1px solid ${theme.colors.text.primaryDarkBackground}`, paddingBottom: 6 }}
                    >
                      Gross APR from rewards (trading fees + incentives), factoring in leverage.
                    </div>
                    <div style={{ paddingTop: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>1h average</div>
                        <div>{getPctValue(metricsData.apr1h.rewardsApr)}</div>
                      </div>
                      {typeof metricsData?.apr24h?.rewardsApr === 'number' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>24h average</div>
                          <div>{getPctValue(metricsData.apr24h.rewardsApr)}</div>
                        </div>
                      )}
                      {typeof metricsData?.aprFull?.rewardsApr === 'number' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>All-time average</div>
                          <div>{getPctValue(metricsData.aprFull.rewardsApr)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ),
              },
            ]
          : []),
        ...(typeof metricsData.apr1h.debtApr !== 'undefined'
          ? [
              {
                value: getPctValue(metricsData.apr1h.debtApr),
                name: 'Debt APR',
                tooltipText: (
                  <div>
                    <div
                      style={{ borderBottom: `1px solid ${theme.colors.text.primaryDarkBackground}`, paddingBottom: 6 }}
                    >
                      Annualized average interest rate paid on borrowed assets.
                    </div>
                    <div style={{ paddingTop: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>1h average</div>
                        <div>{getPctValue(metricsData.apr1h.debtApr)}</div>
                      </div>
                      {typeof metricsData?.apr24h?.debtApr === 'number' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>24h average</div>
                          <div>{getPctValue(metricsData.apr24h.debtApr)}</div>
                        </div>
                      )}
                      {typeof metricsData?.aprFull?.debtApr === 'number' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>All-time average</div>
                          <div>{getPctValue(metricsData.aprFull.debtApr)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ),
              },
            ]
          : []),
      ]
    case 'pnl':
      return [
        {
          value: getFormattedUsdValue(metricsData.pnl.totalPnl),
          name: 'PnL',
          tooltipText:
            'Net result of your position, factoring in yield, price impact, and borrowing costs — your take-home profit or loss.',
          pctDiff: `(${getPctValue(metricsData.pnl.returnOnEquity)})`,
          isValueNegative: metricsData.pnl.returnOnEquity < 0,
        },
        {
          value: getFormattedUsdValue(metricsData.pnl.netYieldValue),
          name: 'Net yield',
          tooltipText:
            'Total rewards and fees earned on your leveraged position (PnL = Net Yield – Price Divergence Impact).',
          pctDiff: `(${getPctValue(metricsData.pnl.yieldOnEquity)})`,
          isValueNegative: metricsData.pnl.yieldOnEquity < 0,
        },
        {
          value: getFormattedUsdValue(metricsData.pnl.divergenceImpact),
          name: 'Price divergence impact',
          tooltipText:
            'Impact of pool price change on PnL, influenced by price direction and the position structure. That is, whether the asset price change increased or decreased PnL, and by how much.',
          pctDiff: `(${getPctValue(metricsData.pnl.divergenceImpactOnEquity)})`,
          isValueNegative: metricsData.pnl.divergenceImpactOnEquity < 0,
        },
        {
          value: getFormattedUsdValue(metricsData.pnl.hodlBenchmarkPnl),
          name: 'HODL benchmark',
          tooltipText: 'The gain or loss versus simply holding the tokens.',
          pctDiff: `(${getPctValue(metricsData.pnl.hodlBenchmarkReturnRatio)})`,
          isValueNegative: metricsData.pnl.hodlBenchmarkReturnRatio < 0,
        },
      ]
    case 'interest-rates':
      return [
        ...(typeof metricsData.debtInterestRates?.x !== 'undefined'
          ? [
              {
                value: getPctValue(metricsData.debtInterestRates.x),
                name: `Interest rate ${xSymbol}`,
                tooltipText: `Current annualized borrow rate for ${xSymbol}.`,
              },
            ]
          : []),
        ...(typeof metricsData.debtInterestRates?.y !== 'undefined'
          ? [
              {
                value: getPctValue(metricsData.debtInterestRates.y),
                name: `Interest rate ${ySymbol}`,
                tooltipText: `Current annualized borrow rate for ${ySymbol}.`,
              },
            ]
          : []),
        ...(typeof metricsData.debtInterestRates?.effective !== 'undefined'
          ? [
              {
                value: getPctValue(metricsData.debtInterestRates.effective),
                name: 'Effective interest rate',
                tooltipText: 'The average rate you’re paying on borrowed tokens, weighted by how much you’ve borrowed.',
              },
            ]
          : []),
      ]
    case 'debt-rewards':
      return [
        {
          value: getFormattedUsdValue(metricsData.pnl.totalDebtIncreaseValue),
          name: 'Total debt increase value',
          tooltipText: 'Accrued interest added to your debt since opening the position.',
        },
        {
          value: getFormattedUsdValue(metricsData.pnl.totalRewardIncreaseValue),
          name: 'Total rewards increase value',
          tooltipText: 'All rewards and fees earned by the position since it was opened.',
        },
      ]
    default:
      return []
  }
}

export const getHistoryMetricsConfig = (
  theme?: DefaultTheme,
  xSymbol?: string,
  ySymbol?: string
): Record<string, { isPct: boolean; label: string; stroke: string }> => ({
  lpSize: {
    isPct: false,
    label: 'Position size',
    stroke: theme?.colors.metricsGraph.option1 || '',
  },
  equity: {
    isPct: false,
    label: 'Equity',
    stroke: theme?.colors.metricsGraph.option2 || '',
  },
  debt: {
    isPct: false,
    label: 'Debt',
    stroke: theme?.colors.metricsGraph.option3 || '',
  },
  lpApr: {
    isPct: true,
    label: 'LP APR',
    stroke: theme?.colors.metricsGraph.option1 || '',
  },
  lpFeesApr: {
    isPct: true,
    label: 'LP fees APR',
    stroke: theme?.colors.metricsGraph.option2 || '',
  },
  lpRewardsApr: {
    isPct: true,
    label: 'LP rewards APR',
    stroke: theme?.colors.metricsGraph.option3 || '',
  },
  apr: {
    isPct: true,
    label: 'APR',
    stroke: theme?.colors.metricsGraph.option1 || '',
  },
  rewardsApr: {
    isPct: true,
    label: 'Rewards APR',
    stroke: theme?.colors.metricsGraph.option2 || '',
  },
  debtApr: {
    isPct: true,
    label: 'Debt APR',
    stroke: theme?.colors.metricsGraph.option3 || '',
  },
  interestRateX: {
    isPct: true,
    label: `Interest rate ${xSymbol}`,
    stroke: theme?.colors.metricsGraph.option1 || '',
  },
  interestRateY: {
    isPct: true,
    label: `Interest rate ${ySymbol}`,
    stroke: theme?.colors.metricsGraph.option2 || '',
  },
  interestRateEffective: {
    isPct: true,
    label: 'Interest rate effective',
    stroke: theme?.colors.metricsGraph.option3 || '',
  },
  totalDebtIncreaseValue: {
    isPct: false,
    label: 'Total debt increase value',
    stroke: theme?.colors.metricsGraph.option1 || '',
  },
  totalRewardIncreaseValue: {
    isPct: false,
    label: 'Total reward increase value',
    stroke: theme?.colors.metricsGraph.option2 || '',
  },
  totalPnl: {
    isPct: false,
    label: 'PnL',
    stroke: theme?.colors.metricsGraph.option1 || '',
  },
  netYieldValue: {
    isPct: false,
    label: 'Net yield',
    stroke: theme?.colors.metricsGraph.option2 || '',
  },
  divergenceImpact: {
    isPct: false,
    label: 'Price divergence impact',
    stroke: theme?.colors.metricsGraph.option3 || '',
  },
  hodlBenchmarkPnl: {
    isPct: false,
    label: 'HODL benchmark',
    stroke: theme?.colors.metricsGraph.option4 || '',
  },
  returnOnEquity: {
    isPct: true,
    label: 'Return on equity',
    stroke: theme?.colors.metricsGraph.option1 || '',
  },
  yieldOnEquity: {
    isPct: true,
    label: 'Yield on equity',
    stroke: theme?.colors.metricsGraph.option2 || '',
  },
  divergenceImpactOnEquity: {
    isPct: true,
    label: 'Divergence impact on equity',
    stroke: theme?.colors.metricsGraph.option3 || '',
  },
  hodlBenchmarkReturnRatio: {
    isPct: true,
    label: 'HODL benchmark return ratio',
    stroke: theme?.colors.metricsGraph.option4 || '',
  },
})

const applyMetricConfig = (value: number | undefined, key: string): number | undefined => {
  if (typeof value !== 'number') {
    return undefined
  }
  const isPct = getHistoryMetricsConfig()[key]?.isPct
  return isPct ? value * 100 : value
}

export const getHistoryMetrics = (
  activeMetric: ActiveMetric,
  metricsHistoryData?: PositionsGetMetricsHistoryResponseType,
  isPct?: boolean
) => {
  if (!metricsHistoryData) {
    return []
  }

  switch (activeMetric) {
    case 'position-size':
      return metricsHistoryData.map(d => ({
        timestampMs: d.timestampMs,
        lpSize: applyMetricConfig(d.metrics.lpSize, 'lpSize'),
        equity: applyMetricConfig(d.metrics.equity, 'equity'),
        debt: applyMetricConfig(d.metrics.debt, 'debt'),
      }))
    case 'lp-apr':
      return metricsHistoryData.map(d => ({
        timestampMs: d.timestampMs,
        lpApr: applyMetricConfig(d.metrics.apr1h?.lpApr, 'lpApr'),
        lpFeesApr: applyMetricConfig(d.metrics.apr1h?.lpFeesApr, 'lpFeesApr'),
        lpRewardsApr: applyMetricConfig(d.metrics.apr1h?.lpRewardsApr, 'lpRewardsApr'),
      }))
    case 'apr':
      return metricsHistoryData.map(d => ({
        timestampMs: d.timestampMs,
        apr: applyMetricConfig(d.metrics.apr1h?.apr, 'apr'),
        rewardsApr: applyMetricConfig(d.metrics.apr1h?.rewardsApr, 'rewardsApr'),
        debtApr: applyMetricConfig(d.metrics.apr1h?.debtApr, 'debtApr'),
      }))
    case 'interest-rates':
      return metricsHistoryData.map(d => ({
        timestampMs: d.timestampMs,
        interestRateX: applyMetricConfig(d.metrics.debtInterestRates?.x, 'interestRateX'),
        interestRateY: applyMetricConfig(d.metrics.debtInterestRates?.y, 'interestRateY'),
        interestRateEffective: applyMetricConfig(d.metrics.debtInterestRates?.effective, 'interestRateEffective'),
      }))
    case 'debt-rewards':
      return metricsHistoryData.map(d => ({
        timestampMs: d.timestampMs,
        totalDebtIncreaseValue: applyMetricConfig(d.metrics.pnl.totalDebtIncreaseValue, 'totalDebtIncreaseValue'),
        totalRewardIncreaseValue: applyMetricConfig(d.metrics.pnl.totalRewardIncreaseValue, 'totalRewardIncreaseValue'),
      }))
    case 'pnl':
      if (isPct) {
        return metricsHistoryData.map(d => ({
          timestampMs: d.timestampMs,
          returnOnEquity: applyMetricConfig(d.metrics.pnl.returnOnEquity, 'returnOnEquity'),
          yieldOnEquity: applyMetricConfig(d.metrics.pnl.yieldOnEquity, 'yieldOnEquity'),
          divergenceImpactOnEquity: applyMetricConfig(
            d.metrics.pnl.divergenceImpactOnEquity,
            'divergenceImpactOnEquity'
          ),
          hodlBenchmarkReturnRatio: applyMetricConfig(
            d.metrics.pnl.hodlBenchmarkReturnRatio,
            'hodlBenchmarkReturnRatio'
          ),
        }))
      }
      return metricsHistoryData.map(d => ({
        timestampMs: d.timestampMs,
        totalPnl: applyMetricConfig(d.metrics.pnl.totalPnl, 'totalPnl'),
        netYieldValue: applyMetricConfig(d.metrics.pnl.netYieldValue, 'netYieldValue'),
        divergenceImpact: applyMetricConfig(d.metrics.pnl.divergenceImpact, 'divergenceImpact'),
        hodlBenchmarkPnl: applyMetricConfig(d.metrics.pnl.hodlBenchmarkPnl, 'hodlBenchmarkPnl'),
      }))
    default:
      return []
  }
}

// ---------------------------------------------------------------------------
// Chart series assembly: history + optional live "bridge to now"
// ---------------------------------------------------------------------------

/**
 * Below this many real datapoints a line chart conveys nothing (and renders
 * degenerately — a single point has a zero-width domain and, with gap-breaking,
 * is invisible). The container shows an empty state instead.
 */
export const MIN_CHART_POINTS = 3

/** Suffix marking synthetic bridge series keys (dashed segment to "now"). */
export const BRIDGE_KEY_SUFFIX = 'LiveBridge'

export const bridgeBaseKey = (bridgeKey: string) => bridgeKey.slice(0, -BRIDGE_KEY_SUFFIX.length)

/**
 * Metrics whose live values are re-marked to current prices on read, so a
 * "now" datapoint is trustworthy. APR / interest-rate / cumulative-accumulator
 * series are served as stored — bridging them would assert a freshness they
 * don't have, so they just end at the last real sample.
 */
const BRIDGEABLE_METRICS: ActiveMetric[] = ['position-size', 'pnl']

export interface ChartSeries {
  data: DataPoint[]
  /** Keys of the synthetic dashed bridge series (empty when no bridge). */
  bridgeKeys: string[]
  /** Number of real (stored) datapoints, before any synthetic augmentation. */
  realPointCount: number
}

/**
 * Builds the chart series for a metric tab: the stored history mapped per tab,
 * plus — for open positions whose last stored point is stale — a synthetic
 * dashed "bridge" from the last real point to the live re-marked values at
 * `Date.now()`. Without it, a position that dropped off the sampling grid
 * (e.g. below the active-notional floor) shows a chart frozen at its last
 * sample while the header shows live values.
 *
 * The bridge is carried in separate `*LiveBridge` keys defined at exactly two
 * timestamps (last real point + now), so the chart can render it as its own
 * dashed line without disturbing the real series or the gap-breaking logic.
 */
export const buildChartSeries = (
  activeMetric: ActiveMetric,
  metricsHistoryData: PositionsGetMetricsHistoryResponseType | undefined,
  isPct: boolean,
  liveMetrics: PositionsGetMetricsResponseType | undefined,
  isActivePosition: boolean
): ChartSeries => {
  const base: DataPoint[] = getHistoryMetrics(activeMetric, metricsHistoryData, isPct)
  const realPointCount = base.length

  if (
    realPointCount < MIN_CHART_POINTS ||
    !isActivePosition ||
    !liveMetrics ||
    !BRIDGEABLE_METRICS.includes(activeMetric)
  ) {
    return { data: base, bridgeKeys: [], realPointCount }
  }

  const nowMs = Date.now()
  const lastReal = base[base.length - 1]
  // Fresh chart (last sample within normal grid cadence) — nothing to bridge.
  if (nowMs - lastReal.timestampMs <= POSITION_METRICS_GAP_THRESHOLD_MS) {
    return { data: base, bridgeKeys: [], realPointCount }
  }

  // Map the live metrics through the same per-tab extraction as history rows
  // (the live response shape is identical to a history row's `metrics`).
  const [liveRow] = getHistoryMetrics(activeMetric, [{ timestampMs: nowMs, metrics: liveMetrics }], isPct)

  const bridgeKeys: string[] = []
  const lastRealAugmented: DataPoint = { ...lastReal }
  const livePoint: DataPoint = { timestampMs: nowMs }
  for (const [key, liveValue] of Object.entries(liveRow)) {
    if (key === 'timestampMs' || typeof liveValue !== 'number') {
      continue
    }
    const anchorValue = lastReal[key]
    // Only bridge series that actually have a value at the anchor point.
    if (typeof anchorValue !== 'number') {
      continue
    }
    const bridgeKey = `${key}${BRIDGE_KEY_SUFFIX}`
    bridgeKeys.push(bridgeKey)
    lastRealAugmented[bridgeKey] = anchorValue
    livePoint[bridgeKey] = liveValue
  }

  if (bridgeKeys.length === 0) {
    return { data: base, bridgeKeys: [], realPointCount }
  }

  return {
    data: [...base.slice(0, -1), lastRealAugmented, livePoint],
    bridgeKeys,
    realPointCount,
  }
}

export const getYDomainWithPadding = (
  data: Record<string, number | undefined>[],
  keys: string[],
  paddingRatio = 0.1
): [number, number] => {
  const values: number[] = []

  for (const row of data) {
    for (const key of keys) {
      const val = row[key]
      if (typeof val === 'number' && isFinite(val)) {
        values.push(val)
      }
    }
  }

  if (values.length === 0) return [0, 1]

  const min = Math.min(...values)
  const max = Math.max(...values)

  const paddedMin = min !== 0 ? min - min * paddingRatio : 0
  const paddedMax = max + max * paddingRatio

  return [paddedMin, paddedMax]
}

export const isDateTickLabel = (label: string) => /\d{1,2}(?:st|nd|rd|th)\s+\w+/.test(label)

export const getFormattedTickValue = (timestamp: number, isFirstTimestamp: boolean, isSingleDayRange: boolean) => {
  const date = new Date(timestamp)

  const day = date.getDate()
  const month = date.toLocaleString('default', { month: 'short' })
  const year = date.getFullYear()
  const hours = date.getHours()
  const minutes = date.getMinutes().toString().padStart(2, '0')

  const ordinal = getOrdinalSuffix(day)
  const isStartOfTheDay = hours === 0 && date.getMinutes() === 0
  const isCurrentYear = year === new Date().getFullYear()

  if (isFirstTimestamp && isSingleDayRange) {
    const time = isStartOfTheDay ? '' : ` ${hours}:${minutes}`
    return isCurrentYear ? `${day}${ordinal} ${month}${time}` : `${day}${ordinal} ${month} ${year}${time}`
  }

  if (isStartOfTheDay) {
    return isCurrentYear ? `${day}${ordinal} ${month}` : `${day}${ordinal} ${month} ${year}`
  }

  return `${hours}:${minutes}`
}
